import jsPDF from 'jspdf';
import { BRAND } from '@/config/brand';
import { 
  QUESTIONNAIRE_CONFIGS, 
  getQuestionnaireType,
  getResponseLabel,
  type QuestionnaireConfig 
} from '@/data/welcomeQuestionnaireData';
import NotoSansRegular from '@/assets/fonts/NotoSans-Regular.ttf';
import NotoSansBold from '@/assets/fonts/NotoSans-Bold.ttf';

// Brand colors using BRAND config
const COLORS = {
  primary: BRAND.colors.gold,
  secondary: BRAND.colors.forestGreen,
  text: '#1a1a1a',
  muted: '#6b7280',
  background: BRAND.colors.lightBackground,
  accent: BRAND.colors.sageGreen,
};

interface QuestionnaireExportData {
  patientName: string;
  patientEmail: string;
  patientPhone: string;
  appointmentDate: string;
  serviceTitle: string;
  questionnaireType: string;
  responses: Record<string, any>;
}

async function loadFont(url: string): Promise<string> {
  const response = await fetch(url);
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export async function generateQuestionnairePDF(data: QuestionnaireExportData): Promise<void> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  let y = margin;

  // Load custom fonts
  try {
    const regularFont = await loadFont(NotoSansRegular);
    const boldFont = await loadFont(NotoSansBold);
    
    doc.addFileToVFS('NotoSans-Regular.ttf', regularFont);
    doc.addFileToVFS('NotoSans-Bold.ttf', boldFont);
    doc.addFont('NotoSans-Regular.ttf', 'NotoSans', 'normal');
    doc.addFont('NotoSans-Bold.ttf', 'NotoSans', 'bold');
  } catch (e) {
    console.warn('Could not load custom fonts, using default');
  }

  const setFont = (style: 'normal' | 'bold' = 'normal', size: number = 10) => {
    try {
      doc.setFont('NotoSans', style);
    } catch {
      doc.setFont('helvetica', style);
    }
    doc.setFontSize(size);
  };

  // Helper to check page break
  const checkPageBreak = (requiredSpace: number): boolean => {
    if (y + requiredSpace > pageHeight - 30) {
      doc.addPage();
      y = margin;
      return true;
    }
    return false;
  };

  // Header with logo
  try {
    const logoUrl = '/regard-de-nature-logo.png';
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    await new Promise<void>((resolve) => {
      img.onload = () => {
        const logoHeight = 20;
        const logoWidth = (img.width / img.height) * logoHeight;
        doc.addImage(img, 'PNG', margin, y, logoWidth, logoHeight);
        resolve();
      };
      img.onerror = () => resolve();
      img.src = logoUrl;
    });
  } catch (e) {
    console.warn('Could not load logo');
  }

  y += 25;

  // Title
  doc.setTextColor(COLORS.secondary);
  setFont('bold', 18);
  doc.text('QUESTIONNAIRE DE BIENVENUE', pageWidth / 2, y, { align: 'center' });
  y += 12;

  // Subtitle with service type
  setFont('normal', 12);
  doc.setTextColor(COLORS.primary);
  doc.text(data.serviceTitle, pageWidth / 2, y, { align: 'center' });
  y += 15;

  // Patient info card
  doc.setFillColor(248, 247, 244);
  doc.roundedRect(margin, y, contentWidth, 35, 3, 3, 'F');
  
  y += 8;
  doc.setTextColor(COLORS.text);
  setFont('bold', 11);
  doc.text('Informations du consultant', margin + 5, y);
  
  y += 8;
  setFont('normal', 10);
  doc.text(`Nom: ${data.patientName}`, margin + 5, y);
  doc.text(`Date du RDV: ${data.appointmentDate}`, margin + contentWidth / 2, y);
  
  y += 6;
  doc.text(`Email: ${data.patientEmail}`, margin + 5, y);
  doc.text(`Téléphone: ${data.patientPhone}`, margin + contentWidth / 2, y);
  
  y += 18;

  // Get questionnaire config
  const questionnaireType = getQuestionnaireType(data.serviceTitle);
  const config = QUESTIONNAIRE_CONFIGS[questionnaireType];

  // Intro text
  doc.setTextColor(COLORS.muted);
  setFont('normal', 9);
  const introLines = doc.splitTextToSize(config.intro, contentWidth);
  doc.text(introLines, margin, y);
  y += introLines.length * 5 + 8;

  // Responses
  doc.setTextColor(COLORS.text);
  
  for (const question of config.questions) {
    const value = data.responses[question.id];
    const detailValue = data.responses[`${question.id}_detail`];
    
    // Skip empty "autre" fields
    if (question.id.endsWith('_autre') && !value) continue;
    
    // Skip conditional "autre" if parent doesn't have "autre" selected
    if (question.id.endsWith('_autre')) {
      const parentId = question.id.replace('_autre', '');
      const parentValue = data.responses[parentId];
      if (!Array.isArray(parentValue) || !parentValue.includes('autre')) continue;
    }
    
    checkPageBreak(25);

    // Question label
    setFont('bold', 10);
    doc.setTextColor(COLORS.secondary);
    const questionLines = doc.splitTextToSize(question.label, contentWidth);
    doc.text(questionLines, margin, y);
    y += questionLines.length * 5 + 3;

    // Response value
    setFont('normal', 10);
    doc.setTextColor(COLORS.text);
    
    let responseText = '';
    
    if (question.type === 'checkbox' && Array.isArray(value)) {
      responseText = value
        .map(v => question.options?.find(opt => opt.id === v)?.label || v)
        .join(', ');
    } else if (question.type === 'scale') {
      responseText = value ? `${value}/10` : 'Non renseigné';
    } else if (question.type === 'conditionalText') {
      const selectedLabel = question.options?.find(opt => opt.id === value)?.label || value;
      responseText = selectedLabel || 'Non renseigné';
      if (value === 'oui' && detailValue) {
        responseText += ` - ${detailValue}`;
      }
    } else if (question.type === 'text') {
      responseText = value || 'Non renseigné';
    } else {
      responseText = String(value || 'Non renseigné');
    }

    const responseLines = doc.splitTextToSize(`→ ${responseText}`, contentWidth - 5);
    doc.text(responseLines, margin + 3, y);
    y += responseLines.length * 5 + 8;
  }

  // Footer note if exists
  if (config.footer) {
    checkPageBreak(25);
    
    y += 5;
    doc.setFillColor(255, 250, 240);
    doc.roundedRect(margin, y, contentWidth, 20, 2, 2, 'F');
    
    y += 6;
    doc.setTextColor(COLORS.muted);
    setFont('normal', 8);
    const footerLines = doc.splitTextToSize(`💡 ${config.footer}`, contentWidth - 10);
    doc.text(footerLines, margin + 5, y);
    y += footerLines.length * 4 + 10;
  }

  // Page footer
  const addFooter = (pageNum: number, totalPages: number) => {
    doc.setTextColor(COLORS.muted);
    setFont('normal', 8);
    doc.text(
      `${BRAND.name.full} – ${BRAND.baseline.full}`,
      pageWidth / 2,
      pageHeight - 12,
      { align: 'center' }
    );
    doc.text(
      `Page ${pageNum}/${totalPages}`,
      pageWidth - margin,
      pageHeight - 12,
      { align: 'right' }
    );
  };

  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(i, totalPages);
  }

  // Save
  const fileName = `Questionnaire_${data.patientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
}
